/**
 * Post-authentication redirect targets.
 *
 * The auth screen accepts a `?next=` parameter so that a user who was sent to
 * sign in from mid-flow returns to where they left off instead of being dumped
 * on the dashboard. That parameter is attacker-controllable: anyone can send a
 * victim a link to `/auth?next=<anything>`.
 *
 * SECURITY: `next` is therefore a KEY INTO AN ALLOWLIST, never a path or URL to
 * navigate to. Passing the raw value to navigate() would be an open redirect on
 * an auth callback — the exact shape used to harvest credentials by bouncing a
 * freshly-authenticated user to a look-alike host. Anything unrecognised falls
 * back to DEFAULT_POST_AUTH. Do not "improve" this by accepting values that
 * merely start with "/": that still permits `//evil.example` (protocol-relative,
 * which browsers resolve as a different ORIGIN) and `/\evil.example`.
 *
 * Adding a destination means adding it here, which is deliberately a code change
 * with a test attached.
 */

/** Where a user lands after auth when no valid `next` was supplied. */
export const DEFAULT_POST_AUTH = '/ideas';

/**
 * Allowlisted destinations, keyed by the opaque token that travels in the URL.
 * Keys are short and meaningless to an attacker — they cannot be pointed
 * anywhere that is not already in this object.
 */
export const POST_AUTH_TARGETS = {
  /** Returning to the anonymous interview's final screen to run the scan. */
  launch: '/launch',
  ideas: '/ideas',
} as const;

export type PostAuthTarget = keyof typeof POST_AUTH_TARGETS;

/**
 * Lookup table with a NULL PROTOTYPE. This is not stylistic: a plain object
 * literal inherits from Object.prototype, so `POST_AUTH_TARGETS['toString']`
 * returns a function — truthy — and a naive `?? DEFAULT` or `in` check would
 * treat "toString", "constructor" and "__proto__" as valid destinations. Both
 * escapes were caught by `navigation.test.ts`; keep the null prototype and the
 * hasOwn check below.
 */
const TARGET_LOOKUP: Record<string, string> = Object.assign(
  Object.create(null) as Record<string, string>,
  POST_AUTH_TARGETS,
);

/**
 * Resolves a `?next=` value to a safe in-app path.
 *
 * Returns DEFAULT_POST_AUTH for anything not explicitly allowlisted, including
 * absolute URLs, protocol-relative URLs, backslash tricks, and encoded variants
 * — none of which can match a key in POST_AUTH_TARGETS.
 */
export function resolveNext(next: string | null | undefined): string {
  if (!next) return DEFAULT_POST_AUTH;
  return TARGET_LOOKUP[next] ?? DEFAULT_POST_AUTH;
}

/** True when `next` names a real allowlisted destination. */
export function isKnownNext(
  next: string | null | undefined,
): next is PostAuthTarget {
  return Boolean(next) && Object.hasOwn(POST_AUTH_TARGETS, next!);
}
